import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { postNamedCommand, postNamedQuery } from '../service'

const DOMAIN = 'dagger'

export const IDENTITY_KEYS = {
  identities: ['delegated-identities'],
  identity: (id) => ['delegated-identity', id],
  eligibleUsers: ['eligible-identity-users']
}

export const useListEligibleIdentityUsers = (options = {}) =>
  useQuery({
    queryKey: IDENTITY_KEYS.eligibleUsers,
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-eligible-identity-users', {})
      return data
    },
    staleTime: 60_000,
    ...options
  })

export const useListDelegatedIdentities = (options = {}) =>
  useQuery({
    queryKey: IDENTITY_KEYS.identities,
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'list-delegated-identities', {})
      return data
    },
    staleTime: 30_000,
    ...options
  })

export const useGetDelegatedIdentity = (delegatedIdentityId, options = {}) =>
  useQuery({
    queryKey: IDENTITY_KEYS.identity(delegatedIdentityId),
    queryFn: async () => {
      const { data } = await postNamedQuery(DOMAIN, 'get-delegated-identity', { delegatedIdentityId })
      return data
    },
    enabled: !!delegatedIdentityId,
    ...options
  })

export const useCreateDelegatedIdentity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'create-delegated-identity', body)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDENTITY_KEYS.identities }).catch(console.error)
    }
  })
}

export const useUpdateDelegatedIdentity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await postNamedCommand(DOMAIN, 'update-delegated-identity', body)
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: IDENTITY_KEYS.identities }).catch(console.error)
      if (variables.delegatedIdentityId) {
        queryClient.invalidateQueries({ queryKey: IDENTITY_KEYS.identity(variables.delegatedIdentityId) }).catch(console.error)
      }
    }
  })
}

export const useDeleteDelegatedIdentity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ delegatedIdentityId }) => {
      const { data } = await postNamedCommand(DOMAIN, 'delete-delegated-identity', { delegatedIdentityId })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDENTITY_KEYS.identities }).catch(console.error)
    }
  })
}

export const useEnableDelegatedIdentity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ delegatedIdentityId }) => {
      const { data } = await postNamedCommand(DOMAIN, 'enable-delegated-identity', { delegatedIdentityId })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDENTITY_KEYS.identities }).catch(console.error)
    }
  })
}

export const useDisableDelegatedIdentity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ delegatedIdentityId }) => {
      const { data } = await postNamedCommand(DOMAIN, 'disable-delegated-identity', { delegatedIdentityId })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDENTITY_KEYS.identities }).catch(console.error)
    }
  })
}
